import { Controller } from 'stimulus';

const STORAGE_KEY = 'impersonationBarPosition';
const RESET_EVENT = 'impersonation-bar:reset';
const HIDDEN_CLASS = 'impersonation-bar--hidden';
const DRAGGING_CLASS = 'impersonation-bar--dragging';
const MOVED_CLASS = 'impersonation-bar--moved';

const clamp = (value) => Math.min(Math.max(value, 0), 1);
const ratio = (offset, space) => (space > 0 ? clamp(offset / space) : 0);

// Run in the browser console to forget the saved position and send the bar back to its corner.
window.resetImpersonationBarPosition = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
  window.dispatchEvent(new CustomEvent(RESET_EVENT));
};

/**
 * Lets staff drag the floating "Stop impersonating" bar around by its handle.
 *
 * The position is saved in localStorage as a fraction of the free space on each axis
 * (0 is the left or top edge, 1 the right or bottom edge), so the bar keeps its spot
 * relative to the nearest corners and stays on screen when the window is resized.
 *
 * @example
 *   div[data-controller="impersonation-bar"
 *       data-action="resize@window->impersonation-bar#reposition
 *                    impersonation-bar:reset@window->impersonation-bar#reset"]
 *     span[data-action="pointerdown->impersonation-bar#grab pointermove->impersonation-bar#drag
 *                       pointerup->impersonation-bar#drop pointercancel->impersonation-bar#drop"]
 */
export default class extends Controller {
  #position = null;
  #grabOffset = null;

  connect() {
    this.#position = this.#storedPosition();
    this.reposition();
    this.element.classList.remove(HIDDEN_CLASS);
  }

  grab(event) {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);

    const { left, top } = this.element.getBoundingClientRect();
    this.#grabOffset = { x: event.clientX - left, y: event.clientY - top };
    this.element.classList.add(DRAGGING_CLASS);
  }

  drag(event) {
    if (!this.#grabOffset) {
      return;
    }

    const { width, height } = this.#freeSpace;
    this.#position = {
      x: ratio(event.clientX - this.#grabOffset.x, width),
      y: ratio(event.clientY - this.#grabOffset.y, height),
    };
    this.reposition();
  }

  drop() {
    if (!this.#grabOffset) {
      return;
    }

    this.#grabOffset = null;
    this.element.classList.remove(DRAGGING_CLASS);

    if (this.#position) {
      this.#savePosition();
    }
  }

  reposition() {
    if (!this.#position) {
      return;
    }

    // Pinned to the top-left corner and moved with a transform, the bar keeps its natural width;
    // with a large `left` it would shrink and wrap its text when the window narrows.
    this.element.classList.add(MOVED_CLASS);

    const { width, height } = this.#freeSpace;
    const { x, y } = this.#position;
    this.element.style.transform = `translate(${x * width}px, ${y * height}px)`;
  }

  reset() {
    this.#position = null;
    this.element.classList.remove(MOVED_CLASS);
    this.element.style.removeProperty('transform');
  }

  get #freeSpace() {
    const { clientWidth, clientHeight } = document.documentElement;

    return {
      width: Math.max(clientWidth - this.element.offsetWidth, 0),
      height: Math.max(clientHeight - this.element.offsetHeight, 0),
    };
  }

  #storedPosition() {
    try {
      const { x, y } = JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? {};

      return Number.isFinite(x) && Number.isFinite(y) ? { x: clamp(x), y: clamp(y) } : null;
    } catch {
      return null;
    }
  }

  #savePosition() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#position));
    } catch {
      // localStorage unavailable
    }
  }
}
